import {
  ActionType,
  DateTimeDisplay,
  getResourceLifecycleStatus,
  InfoBlockProps,
  InfoBlockStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  optimizedRetryOption,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ConsoleContext } from "../../console/ConsoleContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  MYSQL_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  emptyChar,
  ListingTestIds,
  MainTargetId,
  MonochromeIconIds,
  ttlOneMinCaching,
} from "../../constants/uiConstants";
import {
  BackupSummaryTypeEnum,
  MdsBackupSummary,
  MdsBackupSummaryCollection,
  MdsCrashRecoveryStatus,
  MdsCrashRecoveryStatusValues,
} from "../../gen/clients/mchub-azure-api-client-mds";
import { parseId } from "../../helpers/idHelper";
import {
  backupTypeSortComparator,
  getBackupTypeStr,
  getStatusInfo,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { MysqlCreateBackupProps, newMysqlCreateBackup } from "../../operations/Mysql/MysqlCreateBackup";
import { MysqlDeleteBackupProps, newMysqlDeleteBackup } from "../../operations/MysqlBackup/MysqlDeleteBackup";
import { chainFunctions, getOciRegion } from "../../utils";

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.MYSQL_BACKUP_DETAILS].map(config => config.key);
interface MdsBackupSummaryExt extends MdsBackupSummary {
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
}

interface MdsBackupSummaryBackupTypeExt extends MdsBackupSummary {
  backupType: BackupSummaryTypeEnum;
}
export interface MdsBackupsListProps {
  location: string;
  dbSystemId : string;
  crashRecovery: MdsCrashRecoveryStatus | undefined;
}

export enum ActionIds {
  CreateBackup = "create-backup",
  Delete = "Delete",
  Refresh = "refresh",
  Restore = "restore",
}

export enum ActionBarTestIds {
  CreateBackup = "create-backup",
  Delete = "Delete",
  Refresh = "refresh",
  Restore = "restore",
}

export enum ColumnIds {
  Name = "name",
  ResourceStatus = "status",
  Type = "backupType",
  Created = "timeCreated",
}

export enum ColumnTestIds {
  Name = "name",
  ResourceStatus = "status",
  Type = "backupType",
  Created = "timeCreated",
}

export const MysqlBackupsList = (
  { location, dbSystemId, crashRecovery }: MdsBackupsListProps,
): JSX.Element => {
  const { locale } = React.useContext<Settings>(SettingsContext);

  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { navigateTo } = useNavigation(ConsoleContext);

  const [selection, setSelection] = React.useState<MdsBackupSummary[]>([]);
  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  const { subscriptionId, resourceGroup: resourceGroupName, resourceName: dbSystemName } = parseId(dbSystemId);
  const { subListingPageItemCount } = useRowCount();

  const { response: listBackupsResponse, loading: listBackupsLoading, refresh: listBackupsRefresh } = useQueryCall({
    wait: !(subscriptionId && resourceGroupName && location),
    method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.listMdsDbSystemsBackups,
    options: {
      args: {
        subscriptionId,
        resourceGroupName,
        dbSystemName,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadBackups(),
      },
    },
  });

  const backupItems = listBackupsResponse
    && responseItemstoArray<MdsBackupSummaryCollection, MdsBackupSummary>(listBackupsResponse);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => backupItems, [JSON.stringify(backupItems)]);
  const getBackupType = (value: MdsBackupSummary): JSX.Element => (
    <div>{getBackupTypeStr(value?.backupType as BackupSummaryTypeEnum) || emptyChar}</div>
  );

  const getDateTime = (value: Date): JSX.Element => <DateTimeDisplay date={value} />;

  const getStatus = (value: MdsBackupSummaryExt): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.Name,
      testId: ColumnTestIds.Name,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.name}
          navigation={{
            onClick: (): void => {
              navigateTo(
                // eslint-disable-next-line max-len
                `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.MYSQL_BACKUP_DETAILS][0].panelPath}?location=${value.location}`,
                detailsPageRegistrationIds[0],
                { dbSystemId },
              );
            },
          }}
        />
      ),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: MdsBackupSummaryExt,
        b: MdsBackupSummaryExt,
      ) => statusSortComparator<MdsBackupSummaryExt>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.Type,
      testId: ColumnTestIds.Type,
      name: Messages.labels.type(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: MdsBackupSummaryBackupTypeExt,
        b: MdsBackupSummaryBackupTypeExt,
      ) => backupTypeSortComparator<MdsBackupSummaryBackupTypeExt>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getBackupType(value),
    },
    {
      itemProp: ColumnIds.Created,
      testId: ColumnTestIds.Created,
      name: Messages.labels.created(),
      flexGrow: 1,
      initialSortDirection: SortDirections.DESC,
      isResizable: true,
      onRenderItems: value => getDateTime(value.timeCreated),
    },
  ];

  const { trigger: triggerCreateBackup } = useOperation<MysqlCreateBackupProps>(newMysqlCreateBackup);
  const { trigger: triggerDeleteBackup } = useOperation<MysqlDeleteBackupProps>(newMysqlDeleteBackup);
  const { closePanels } = useSidePanel();

  const backupActions: ActionType[] = [
    {
      key: ActionIds.CreateBackup,
      testId: ActionBarTestIds.CreateBackup,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      disabled: crashRecovery === MdsCrashRecoveryStatusValues.DISABLED,
      onClick: () => {
        trackActionClick(ActionIds.CreateBackup, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS);
        listingComponentRef?.resetTableSelection();
        triggerCreateBackup({
          targetId: MainTargetId,
          dbSystemId,
          location,
          onExecute: listBackupsRefresh,
          onCancel: () => trackActionDiscard(ActionIds.CreateBackup, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS),
        });
      },
    },
    {
      key: ActionIds.Restore,
      testId: ActionBarTestIds.Restore,
      text: Messages.actions.restoreToNewDbSystem(),
      icon: MonochromeIconIds.Database,
      disabled: !selection.length || selection[0]?.status !== "Active",
      onClick: () => {
        trackActionClick(ActionIds.Restore, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS);
        const backupId = selection[0]?.id;
        navigateTo(MYSQL_CREATE_ROUTE, PageId.MYSQL_CREATE, {
          subscriptionId,
          backupId,
          backupName: selection[0]?.name,
          backupDataStorageSizeInGBs: selection[0]?.dataStorageSizeInGBs,
          backupMysqlVersion: selection[0]?.mysqlVersion,
          dbSystemName,
          analytics: {
            pageId: PageId.MYSQL_DETAILS,
            panelId: DetailsPanelId.BACKUPS,
            actionName: ActionIds.Restore,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS);
        chainFunctions(listBackupsRefresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: !selection.length,
      title: Messages.labels.deleteBackup(),
      description: Messages.hints.deleteBackupConfirmation(selection[0]?.name || ""),
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS);
      },
      onConfirm: () => {
        triggerDeleteBackup({
          backupId: selection[0].id,
          location,
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            listBackupsRefresh();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.MYSQL_DETAILS, DetailsPanelId.BACKUPS),
    },
  ];

  const disabledCrashRecoveryInfoBlocks: InfoBlockProps[] = crashRecovery !== MdsCrashRecoveryStatusValues.DISABLED
    ? []
    : [{
      message: Messages.detailMysqlBackup.warning(),
      messageType: InfoBlockStatus.INFO,
    }];

  return (
    <Listing
      testId={ListingTestIds.MysqlBackups}
      listingComponentRef={setListingComponentRef}
      itemsPerPage={subListingPageItemCount}
      items={items || []}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemKey={ColumnIds.Name}
      actionBarItems={backupActions}
      infoBlocks={disabledCrashRecoveryInfoBlocks}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      isLoading={listBackupsLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Created,
      }}
    />
  );
};
